// Typed fetch wrapper for the bloomz-status public JSON API.
// Shapes match CONTRACTS.md §3 exactly — keep this file in sync with that doc.

export type ComponentStatus =
  | 'operational'
  | 'degraded_performance'
  | 'partial_outage'
  | 'major_outage'
  | 'under_maintenance';

export type IncidentSeverity = 'sev1' | 'sev2' | 'sev3';

export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved';

export interface StatusComponent {
  id: string;
  name: string;
  status: ComponentStatus;
  updated_at: string;
  /** False when nothing backs `status` (no fresh probe or SLI push, no incident):
   *  it is then only the default 'operational'. Absent from older index.json files. */
  monitored?: boolean;
}

export interface IncidentUpdate {
  status: IncidentStatus;
  body: string;
  posted_at: string;
}

export interface ActiveIncident {
  id: string;
  title: string;
  severity: IncidentSeverity;
  status: IncidentStatus;
  component_ids: string[];
  started_at: string;
  updates: IncidentUpdate[];
}

export interface MaintenanceWindow {
  id: string;
  title: string;
  component_ids: string[];
  starts_at: string;
  ends_at: string;
}

export interface IndexJson {
  generated_at: string;
  overall_status: ComponentStatus;
  components: StatusComponent[];
  active_incidents: ActiveIncident[];
  upcoming_maintenance: MaintenanceWindow[];
}

export interface PostIncidentReport {
  what_happened: string;
  impact_window: string;
  affected_components: string[];
  fix: string;
  prevention: string;
  published_at: string;
}

export interface IncidentDetail extends ActiveIncident {
  resolved_at?: string;
  post_incident_report?: PostIncidentReport;
}

export interface UptimeDay {
  date: string;
  uptime_pct: number;
}

/** One key per component id, ~90 entries oldest-first. */
export type UptimeJson = Record<string, UptimeDay[]>;

export type SubscribeChannel = 'email' | 'slack';

export interface SubscribeRequest {
  channel: SubscribeChannel;
  target: string;
}

export interface SubscribeResponse {
  ok: boolean;
  message?: string;
}

class StatusApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'StatusApiError';
    this.status = status;
  }
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!res.ok) {
    throw new StatusApiError(`GET ${path} failed with ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

/** Same-origin — served statically alongside the SPA. */
export function fetchIndex(): Promise<IndexJson> {
  return getJson<IndexJson>('/index.json');
}

export function fetchUptime(): Promise<UptimeJson> {
  return getJson<UptimeJson>('/uptime.json');
}

export function fetchIncidentDetail(id: string): Promise<IncidentDetail> {
  return getJson<IncidentDetail>(`/incidents/${encodeURIComponent(id)}.json`);
}

export async function postSubscribe(body: SubscribeRequest): Promise<SubscribeResponse> {
  const res = await fetch('/api/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let message = `Subscribe failed with ${res.status}`;
    try {
      const errBody = (await res.json()) as { message?: string };
      if (errBody?.message) message = errBody.message;
    } catch {
      // ignore — non-JSON error body
    }
    throw new StatusApiError(message, res.status);
  }
  try {
    return (await res.json()) as SubscribeResponse;
  } catch {
    return { ok: true };
  }
}

export { StatusApiError };
